// Live per-category stats — Phase 3.
//
// Each chart calls one of the Phase-3 queries via the Tauri Metabase client
// and gets back a per-category map. Charts use the live value when present
// and fall back to the static value in categories.ts when the query is
// loading, errored, or running in browser-dev (no Tauri shell).
//
// The queries live in src-tauri/resources/cohorts/query-catalog.json:
//   live-spend-share-by-category      → D1 + SYM spend
//   live-retention-14d-by-category    → D2
//   live-first-redeem-active-share    → S2 clearance

import { useEffect, useState } from "react";

import { runCatalogQuery } from "./query-catalog";

export type LiveState = "loading" | "live" | "fallback" | "error";

export interface LiveCategoryStats {
  byCode: Record<string, number>;
  state: LiveState;
  error?: string;
}

const EMPTY: LiveCategoryStats = { byCode: {}, state: "loading" };

/**
 * Hook that loads the named query once on mount and returns the per-category
 * value map. The query is expected to return rows where the first column is
 * a category code (matching `Category.code`) and the second is the numeric
 * value to surface.
 */
export function useLiveCategoryStats(queryId: string): LiveCategoryStats {
  const [stats, setStats] = useState<LiveCategoryStats>(EMPTY);

  useEffect(() => {
    if (!queryId) {
      setStats({ byCode: {}, state: "fallback" });
      return;
    }
    let alive = true;
    setStats(EMPTY);
    (async () => {
      try {
        const result = await runCatalogQuery(queryId);
        if (!alive) return;
        if (result.status !== "live") {
          setStats({ byCode: {}, state: "fallback" });
          return;
        }
        const byCode: Record<string, number> = {};
        for (const row of result.rows) {
          const code = String(row[0] ?? "").trim();
          const val = Number(row[1]);
          if (code && Number.isFinite(val)) byCode[code] = val;
        }
        setStats({ byCode, state: "live" });
      } catch (e) {
        if (!alive) return;
        setStats({ byCode: {}, state: "error", error: String(e) });
      }
    })();
    return () => {
      alive = false;
    };
  }, [queryId]);

  return stats;
}

/**
 * Compute share % from a count map. Used for the spend-distribution chart
 * where the query returns counts and we need percentages.
 */
export function toSharePct(byCode: Record<string, number>): Record<string, number> {
  const total = Object.values(byCode).reduce((s, n) => s + n, 0);
  if (total <= 0) return byCode;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(byCode)) {
    out[k] = (v / total) * 100;
  }
  return out;
}
